import interact from 'interactjs';
import { regionUI } from 'lib/region-ui';
import { appUI } from 'lib/app-ui';
import EventTracker from 'lib/event-tracking';

class CommentsUI {

  constructor() {
    this._showCommentContainer = {};
    this._showComment = {};

    this._unseenComments = [];

    localStorage.getItem('showComments') === null || localStorage.getItem('showComments') === 'true'
      ? this._showComments = 'true'
      : this._showComments = 'false';
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  initUnseenComments() {
    // grab comments added since the user last visited this map
    if (!window.recentActivity || !window.recentActivity.notifications) return;
    const unseenNotifications = window.recentActivity.notifications.filter(n => n.unseen);

    if (!unseenNotifications || !unseenNotifications.length) return;
    let unseenOperationIds = {};
    for (const n of unseenNotifications) {
      if (!n.opIds) continue;
      for (const opId of n.opIds) {
        unseenOperationIds[opId] = 1;
      }
    }

    const newCommentIds = [];
    for (const msg of window.recentActivity.operations) {
      if (unseenOperationIds[msg.opId] && msg.operation === 'addComment' && msg.comment && msg.comment.id && (window.user && msg.comment.userId !== window.user.id)) {
        this.addUnseenComment(msg.comment.id);
      }
    }

    this.canvas.appHeaderTag.update();
    this.canvas.commentsSidebarTag && this.canvas.commentsSidebarTag.update();
  }

  hasUnseenComments() {
    return this._unseenComments.length > 0;
  }

  isUnseenComment(commentId) {
    return this._unseenComments.find(id => id === commentId);
  }

  addUnseenComment(commentId) {
    if (!commentId) return;
    this._unseenComments.push(commentId);
  }

  clearUnseenComments() {
    this._unseenComments = [];
    this.canvas.appHeaderTag.update();
    this.canvas.commentsSidebarTag.update();
  }

  flagCommentSeen(commentId) {
    if (!commentId) return;
    this._unseenComments = this._unseenComments.filter(id => id !== commentId);
  }

  isShownCommentContainer(nodeId) {
    return this._showCommentContainer[nodeId];
  }

  isShownComment(commentId) {
    return this._showComment[commentId];
  }

  hideComment(commentId) {
    delete this._showComment[commentId];

    if (this.canvas.commentTags[commentId]) {
      this.canvas.commentTags[commentId].update();
    }
  }

  showComment(commentId) {
    this._showComment[commentId] = true;

    if (this.canvas.commentTags[commentId]) {
      this.canvas.commentTags[commentId].update();
    }
  }

  hideOpenReplyInput({ newCommentInput }) {
    if (newCommentInput && newCommentInput.isOpen) {
      newCommentInput.hide({ save: true });
    }
  }

  hideOpenReplyInputs() {
    const { canvas } = this;
    const { commentTags, commentTagsInSidebar } = canvas;

    for (const tag of Object.values(commentTags)) {
      this.hideOpenReplyInput(tag);
    }

    for (const tag of Object.values(commentTagsInSidebar)) {
      this.hideOpenReplyInput(tag);
    }
  }

  hideCommentContainer(nodeId) {
    delete this._showCommentContainer[nodeId];

    this.unsetNewCommentOnNode();
    const comments = this.canvas.store.nodeTopComments(nodeId);

    // dismiss any open reply inputs on container comments
    for (const c of comments) {
      const commentTag = this.canvas.commentTags[c.id];
      if (commentTag) {
        if (commentTag && commentTag.tags['new-comment-input']) {
          commentTag.tags['new-comment-input'].hide({ save: true });
        }
      }
    }
    if (this.canvas.commentContainerTags[nodeId]) {
      this.canvas.commentContainerTags[nodeId].update();
    }
    if (this.canvas.nodeTags[nodeId]) {
      this.canvas.nodeTags[nodeId].updateLayers();
    }
  }

  showCommentContainer(nodeId) {
    const commentContainer = this.canvas.commentContainerTags[nodeId];

    this._showCommentContainer[nodeId] = true;

    if (commentContainer) {
      commentContainer.update();
    }
    if (this.canvas.nodeTags[nodeId]) {
      this.canvas.nodeTags[nodeId].update({ forceUpdate: true });
    }
  }

  toggleCommentLayer() {
    const { canvas } = this;

    if (canvas.sidebarStatus !== 'comments') {
      canvas.openSidebar('comments');
      this._clearUnseenCommentsTimeout = setTimeout(() => {
        this.clearUnseenComments();
      },10000);

      canvas.commentsSidebarTag.scrollToBottom();

      EventTracker.track({
        category: 'Comments-Sidebar',
        action: 'Toggle-Comments-Sidebar',
        label: 'Open-Comments-Sidebar',
      });
    } else {
      clearTimeout(this._clearUnseenCommentsTimeout);
      this.clearUnseenComments();
      canvas.closeSidebar();
      EventTracker.track({
        category: 'Comments-Sidebar',
        action: 'Toggle-Comments-Sidebar',
        label: 'Close-Comments-Sidebar',
      });
    }
  }

  allCommentsShown() {
    if (this.canvas.store.comments.length === 0) return false;

    for (const comment of this.canvas.store.comments) {
      const target = comment.getTarget();

      if (target === 'node') {
        if (!this.isShownCommentContainer(comment.getNodeId())) {
          return false;
        }
      } else if (target !== 'reply') {
        if (!this.isShownComment(comment.id)) {
          return false;
        }
      }
    }

    return true;
  }

  showAllComments() {
    for (const comment of this.canvas.store.comments) {
      const target = comment.getTarget();

      if (target === 'node') {
        this.showCommentContainer(comment.getNodeId());
      } else if (target !== 'reply') {
        this.showComment(comment.id);
      }
    }
  }

  hideAllComments() {
    const shownCommentContainerNodeIds = Object.keys(this._showCommentContainer);
    for (const nodeId of shownCommentContainerNodeIds) {
      this.hideCommentContainer(nodeId);
    }

    const { canvasCommentTags, commentsLayerTag } = this.canvas;

    for (const id in canvasCommentTags) {
      canvasCommentTags[id].dismiss();
    }

    if (!this.showComments()) {
      commentsLayerTag.update();
    }
  }

  startingNewCommentOnNode(id) {
    return this.startedCommentOnNodeId === id;
  }

  unsetNewCommentOnNode() {
    delete this.startedCommentOnNodeId;
  }

  startNewComment({ position, node, selection, panelPosition }) {
    if (node) {
      const nodeTag = this.canvas.nodeTags[node.id];
      if (!nodeTag) return;
      this.nodeTag = nodeTag;
      this.startedCommentOnNodeId = node.id;
      nodeTag.update();
      nodeTag.updateLayers();
      const commentsContainerTag = nodeTag.tags['comments-container'];
      commentsContainerTag.update();
      commentsContainerTag.tags['popover'].tags['new-comment-input'].show();

    } else {
      this.canvas.newCommentPanel.summon({ position, node, selection, panelPosition });
    }
  }

  highlightCommentContent(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;
    const node = this.canvas.store.getCommentNode(commentId);
    if (node) {
      appUI.highlightElements({nodeIds: [node.id]}, 'rgb(0, 119, 241)');
      return;
    }

    const selection = this.canvas.store.getCommentSelection(commentId);
    if (selection) {
      appUI.highlightElements(selection, 'rgb(0, 119, 241)');
    }
  }

  unhighlightCommentContent() {
    appUI.unhighlightElements();
  }

  resetDragData() {
    this.prevCommentPositions = {};
  }

  setPrevCommentPosition(comment) {
    const { displayProperties } = comment;
    this.prevCommentPositions[comment.id] = Object.assign({},displayProperties.position);
  }

  setupDragging() {
    /* can move and resize; can't create a selection box over the region */
    if (this.dragger) this.dragger.off();

    this.dragger = interact('.draggable-comment')
      .styleCursor(false)
      .actionChecker((pointer, event, action, interactable, element, interaction) => {
        // if dragging isn't enabled, then the user can use the selection area on a region
        if (action.name == 'drag' && element.getAttribute('data-move-enabled') != 'true') {
          action.name = null;
        }

        return action;
      })
      .draggable({
        max: 3,
        maxPerElement: 3,
        onstart: this.onMoveStart.bind(this),
        onmove: this.onMove.bind(this),
        onend: this.onMoveEnd.bind(this),
      });
  }

  onMoveStart(e) {
    const commentID = e.target.getAttribute('data-comment-id');
    const dragComment = canvas.canvasCommentTags[commentID];
    this.dragComment = dragComment;

    // if ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) {
    //   if (!canvas.inTheMiddleOfMetaDrag) {
    //     canvas.inTheMiddleOfMetaDrag = true;
    //     //dragComment.toggleSelected(e);
    //   }
    // }

    this.dragComment.onMoveStart(e);
  }

  onMove(e) {
    e.stopImmediatePropagation();
    this.dragComment.onMove(e);
  }

  onMoveEnd(e) {
    if (!this.dragComment) return;
    this.dragComment.onMoveEnd(e);
    this.dragComment = null;
  }

  getCommentPositionOperationMessage(comment) {
    return this.canvas.operation.messages.updateCommentDisplayProperties({
      uuid: comment.id,
      prevDisplayProperties: {
        position: this.prevCommentPositions[comment.id]
      },
      displayProperties: {
        position: comment.displayProperties.position
      },
    });
  }

  applyMove({ draggingComment, dx, dy }) {
    const position = draggingComment.getPosition();

    draggingComment.setPosition({
      x: position.x + dx,
      y: position.y + dy,
    });
  }

  getTotalReplyAncestorCount(commentId) {
    return 1 + this.canvas.store.getReplyComments(commentId).length; // TODO: recursive count
  }

  getTotalCommentCountOnNode(nodeId) {
    const comments = this.canvas.store.nodeTopComments(nodeId);
    let total = 0;
    for (const comment of comments) {
      total++;
      total += this.canvas.store.getReplyComments(comment.id).length;
    }
    return total;
  }

  toggleShowComments() {
    this._showComments = this._showComments === 'true' ? 'false' : 'true';

    if (this._showComments === 'false') {
      // hide open comments when toggling off comments in the sidebar
      this.hideAllComments();
    }

    localStorage.setItem('showComments', this._showComments);
    this.freshAllCanvasComments();

    EventTracker.track({
      category: 'Comments-Sidebar',
      action: 'Toggle-Show-Comments',
      label: this._showComments === 'true' ? 'Show-Comments' : 'Hide-Comments',
    });
  }

  /*
   refresh all comments in cards or on the canvas
   (use when toggling on/off all comments)
  */
  freshAllCanvasComments() {
    const { canvas } = this;
    if (canvas.commentsLayerTag) canvas.commentsLayerTag.update();

    for (const comment of canvas.store.comments) {
      if (comment.getTarget() === 'node') {
        const node = canvas.store.getCommentNode(comment.id);
        if (node && canvas.nodeTags[node.id]) {
          canvas.nodeTags[node.id].updateLayers();
        }
      }
    }
  }

  showComments() {
    return this._showComments === 'true' ? true : false;
  }

  commentText(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;
    return comment.getText();
  }


  isValidTarget(target) {
    const targets = {
      map: 1,
      sidebar: 1,
      node: 1,
      map: 1,
      reply: 1,
    };
    return Boolean(targets[target]);
  }

  startNodeComment(nodeId) {
    this.showCommentContainer(nodeId);
    this.startNewComment({
      node: this.canvas.store.getNode(nodeId),
    });
  }

  startCanvasComment({ position }) {
    const { canvas } = this;

    const nodeIds = canvas.getSelectedNodes().map(n => n.id);
    const linkIds = canvas.getSelectedLinks().map(l => l.id);
    const regionIds = regionUI.getSelectedRegionIds();

    let selection;
    if (nodeIds.length || linkIds.length || regionIds.length) {
      selection = {nodeIds,linkIds,regionIds};
    }

    let commentsAtPosition = this.canvas.store.getCommentsNearPosition(position);
    let counter = 0;
    while (commentsAtPosition.length) {
      position.x += 30;
      counter++;
      commentsAtPosition = this.canvas.store.getCommentsNearPosition(position);
      if (counter > 100) break;
    }

    this.startNewComment({
      selection,
      position,
    });
  }

  addComment({ commentText, target, selection, node, position, parentId }) {
    const { canvas } = this;
    const displayProperties = {};
    const { commentsSidebarTag } = canvas;

    if (!this.isValidTarget(target)) {
      console.error('invalid comment target');
      return;
    }

    displayProperties.target = target;

    if (target === 'map') {
      displayProperties.position = position;
    }

    if (node) {
      displayProperties.mapNodeUUID = node.id;
    }

    if (selection) {
      displayProperties.selection = selection;
    }

    const comment = canvas.store.addComment({
      commentText,
      displayProperties,
      userId: window.user.id,
      createdTime: new Date(),
      parentId,
    });

    canvas.operation.addComment({
      uuid: comment.id,
      comment: {
        id: comment.id,
        commentText,
        displayProperties,
        parentId,
        userId: window.user.id,
      }
    });

    if (displayProperties.target === 'map') {
      this.showComment(comment.id);
    }

    this.freshenComment(comment);

    // if this is a reply; need to freshen then expand
    if (this.isReply(comment.id)) {
      const topLevelComment = comment.getTopLevelComment();

      this.expandCommentReplies(comment.parentId);

      if (topLevelComment.isResolved()) {
        this.unresolveComment(comment.parentId);
      }
    }

    if (commentsSidebarTag && !parentId) {
      commentsSidebarTag.scrollToBottom({ behavior: 'smooth', block: 'end' });
    }
  }


  deleteComment(commentId,e) {
    if (e) {
      e.stopPropagation();
    }
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    comment.delete();

    this.canvas.operation.deleteComment({
      uuid: comment.id,
    });

    this.freshenComment(comment);
  }

  resolveComment(commentId,e) {
    if (e) {
      e.stopPropagation();
    }
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    comment.resolve();

    this.canvas.operation.resolveComment({
      uuid: comment.id,
    });

    this.freshenComment(comment);
  }


  unresolveComment(commentId,e) {
    if (e) {
      e.stopPropagation();
    }
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    comment.unresolve();

    this.canvas.operation.unresolveComment({
      uuid: comment.id,
    });

    this.freshenComment(comment);
  }

  vote(commentId, val) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    const userId = window.user.id;
    const prevVotes = {};
    const currentVote = this.getCurrentVote(comment);
    const voteUserToken = this.voteUserToken();
    prevVotes[voteUserToken] = currentVote;

    const votes = {};

    if (currentVote === val) {
      // unset current vote
      votes[voteUserToken] = 0;
      comment.setVote(voteUserToken, 0);
    } else {
      votes[voteUserToken] = val;
      comment.setVote(voteUserToken, val);
    }

    canvas.operation.updateCommentDisplayProperties({
      uuid: comment.id,
      prevDisplayProperties: {
        votes: prevVotes
      },
      displayProperties: {
        votes
      },
    });

    this.freshenComment(comment);
  }

  voteUserToken() {
    return `u_${window.user.id}`; // JSON_SET for mysql doesn't like integer keys
  }

  getCurrentVote(comment) {
    if (comment.displayProperties.votes && comment.displayProperties.votes[this.voteUserToken()]) {
      return comment.displayProperties.votes[this.voteUserToken()];
    }
    return 0;
  }

  wasUpvoted(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    const votes = comment.displayProperties.votes;
    if (!votes) return false;
    if (votes[this.voteUserToken()] && votes[this.voteUserToken()] > 0) return true;
  }

  wasDownvoted(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    const votes = comment.displayProperties.votes;
    if (!votes) return false;
    if (votes[this.voteUserToken()] && votes[this.voteUserToken()] < 0) return true;
  }

  getUpvoteCount(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    const votes = comment.displayProperties.votes;

    if (!votes) return '';
    let count = 0;

    for (const userToken in votes) {
      if (votes[userToken] > 0) {
        count++;
      }
    }

    return count;
  };

  getDownvoteCount(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    const votes = comment.displayProperties.votes;

    if (!votes) return '';
    let count = 0;

    for (const userToken in votes) {
      if (votes[userToken] < 0) {
        count++;
      }
    }

    return count;
  };

  getUpvoteNames(commentId) {
    const names = [];

    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return names;

    const votes = comment.displayProperties.votes;
    if (!votes) return names;

    for (const u in votes) {
      if (votes[u] <= 0) continue;

      const id = parseInt(u.replace(/u_/,''), 10);

      if (id) {
        const user = this.getUserInfo(id);
        names.push(user.display_name);
      }
    }

    return names;
  };

  getDownvoteNames(commentId) {
    const names = [];

    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return names;

    const votes = comment.displayProperties.votes;
    if (!votes) return names;

    for (const u in votes) {
      if (votes[u] >= 0) continue;

      const id = parseInt(u.replace(/u_/,''), 10);

      if (id) {
        const user = this.getUserInfo(id);
        names.push(user.display_name);
      }
    }

    return names;
  };

  refreshVoteNames(comment) {
    const commentTag = this.canvas.commentTags[comment.id];
    const commentTagInSidebar = this.canvas.commentTagsInSidebar[comment.id];
    for (const tag of [commentTag,commentTagInSidebar]) {
      if (!tag) continue;
      if (tag.refs.upvoteButton) {
        tag.refs.upvoteButton.setAttribute('data-tooltip',this.getUpvoteNames(comment.id).join(', '));
      }
      if (tag.refs.downvoteButton) {
        tag.refs.downvoteButton.setAttribute('data-tooltip',this.getDownvoteNames(comment.id).join(', '));
      }
    }
  }

  freshenComment(comment) {
    if (!comment) return;

    const commentTag = this.canvas.commentTags[comment.id];
    if (commentTag) {
      commentTag.update();
      commentTag.updateTextDisplay();
    }
    const commentTagInSidebar = this.canvas.commentTagsInSidebar[comment.id];
    if (commentTagInSidebar) {
      commentTagInSidebar.update();
      commentTagInSidebar.updateTextDisplay();
    }
    this.refreshVoteNames(comment);

    const topLevelComment = comment.getTopLevelComment();
    if (topLevelComment.getTarget() === 'node') {
      const node = this.canvas.store.getCommentNode(comment.id);

      if (node) {
        const container = canvas.commentContainerTags[node.id];
        if (container) container.update();
        const nodeTag = this.canvas.nodeTags[node.id];
        if (nodeTag) nodeTag.updateLayers();

        // make comment icon disappear from card if all comments are deleted
        if (topLevelComment.isPurged() || topLevelComment.isResolved()) {
          this.hideCommentContainer(node.id);
        }
      }
    }

    if (this.canvas.commentsSidebarTag) this.canvas.commentsSidebarTag.update();
    if (this.canvas.commentsLayerTag) this.canvas.commentsLayerTag.update();

    this.canvas.appHeaderTag.update();
  }

  isReply(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    return comment.getTarget() === 'reply';
  }

  canReply(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    if (this.canvas.isViewOnly() && !this.canvas.canCommentOnly()) return false;

    return true;
  }

  getReplies(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return [];

    return canvas.store.getReplyComments(comment.id);
  }

  isDeleted(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    return comment.isDeleted;
  }

  getOwnerInfo(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;

    return this.getUserInfo(comment.getUserId());
  }

  getUserInfo(userId) {
    if (window.user && userId === window.user.id) {
      return window.user;
    }

    if (this.canvas.people.collaborators && this.canvas.people.collaborators.length) {
      const collab = this.canvas.people.collaborators.find(p => p.user_id === userId);
      if (collab) return collab.user;
    }

    if (this.canvas.presence && this.canvas.presence.presence[userId]) {
      return this.canvas.presence.presence[userId];
    }

    return { avatar_url: '/images/avatar.png' };
  };

  getDirectCommentUrl(commentId) {
    const {config, access} = this.canvas;
    let url;
    if (access.editKey) {
      url = `${config.site_url}/maps/${access.publicIdentifier}/edit/${access.editKey}?commentId=${commentId}`;
    } else {
      url = `${config.site_url}/maps/${access.publicIdentifier}?commentId=${commentId}`;
    }
    return url;
  }

  panToComment(commentId, openComment=false) {
    if (!commentId) return;
    const _instance = this.canvas.store.getComment(commentId);

    if (!_instance) return;
    const instance = _instance.getTopLevelComment();

    if (instance.getTarget() === 'map') {
      const {x, y} = instance.displayProperties.position;
      this.canvas.enablePanZoom();
      this.canvas.panZoomTo([y, x, y, x], 1, { animate: true });
    } else if (instance.getTarget() === 'sidebar') {
      this.canvas.openSidebar('comments');
      this.scrollToComment(instance.id);

    } else if (instance.getTarget() === 'node') {
      const node = canvas.store.getCommentNode(instance.id);
      if (!node) return;
      const nodeTag = canvas.nodeTags[node.id];
      if (!nodeTag) return;
      this.canvas.enablePanZoom();
      this.canvas.centerContent({ nodes: [node] }, { animate: true });
    }

    if (openComment) {
      this.showCommentInCanvas(commentId);
    }
  }

  showCommentInCanvas(commentId) {
    if (!commentId) return;
    const _instance = this.canvas.store.getComment(commentId);
    if (!_instance) return;
    const instance = _instance.getTopLevelComment();
    if (instance.getTarget() === 'map') {
      const canvasCommentTag = this.canvas.canvasCommentTags[instance.id];
      if (canvasCommentTag) {
        canvasCommentTag.show();
      }
    } else if (instance.getTarget() === 'node') {

      const node = canvas.store.getCommentNode(instance.id);
      if (!node) return;
      this.showCommentContainer(node.id);
    }
  }

  scrollToComment(commentId) {
    const sidebarComment = this.canvas.commentTagsInSidebar[commentId];
    if (!sidebarComment) return;
    const alignToTop = this.sidebarCommentIsInView(sidebarComment);
    if (alignToTop !== null) {
      sidebarComment.root.scrollIntoView(alignToTop);
    }
  }

  sidebarCommentIsInView(commentId) {
    const sidebarComment = this.canvas.commentTagsInSidebar[commentId];
    if (!sidebarComment) return;

    const scrollElement = sidebarComment.parentElement.parentElement;
    const containerTop = $(scrollElement).scrollTop();
    const containerBottom = containerTop + $(scrollElement).height();
    const rowTop = $(sidebarComment).offset().top - $(scrollElement).offset().top;
    const rowBottom = rowTop + $(sidebarComment).height();

    if (rowTop < 0) {
      // scroll up
      return true;
    }

    if (rowBottom + containerTop > containerBottom) {
      // scroll down
      return false;
    }

    // no scroll
    return null;
  }

  expandCommentReplies(commentId) {
    const { commentTags, commentTagsInSidebar } = this.canvas;
    const commentTag = commentTags[commentId];
    const commentTagInSidebar = commentTagsInSidebar[commentId];

    if (commentTag && commentTag.tags['comment-replies']) {
      commentTag.tags['comment-replies'].expand();
    }

    if (commentTagInSidebar && commentTagInSidebar.tags['comment-replies']) {
      commentTagInSidebar.tags['comment-replies'].expand();
    }
  }

  isOwner(commentId) {
    const comment = this.canvas.store.getComment(commentId);
    if (!comment) return;
    return window.user && window.user.id === comment.userId;
  }
}

const commentsUI = new CommentsUI;
window.commentsUI = commentsUI;
export { CommentsUI, commentsUI };



// WEBPACK FOOTER //
// ./starling/src/lib/comments-ui.js